import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { loadArrivals, syncArrivals, clearArrivals } from '../lib/arrivals';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

/**
 * Who is signed in, remembered synchronously.
 *
 * The "acting as" selection is stored per user, but on the first render the user hasn't been
 * fetched yet — which is how a shared browser (the Labs machine, a demo laptop) let a brand-new
 * account inherit the previous person's subject and land on "their FuturePath is theirs to set up".
 * Writing the id down at sign-in means the subject scope can be resolved correctly before the
 * first request goes out.
 */
const rememberUser = (id) => {
  try {
    const previous = localStorage.getItem('futurepath_uid');
    if (previous && previous !== id) clearSubjectSelections();
    if (id) localStorage.setItem('futurepath_uid', id);
  } catch { /* private mode */ }
  if (id) loadArrivals(id);
};

const clearSubjectSelections = () => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith('fp_active_subject_'))
      .forEach((k) => localStorage.removeItem(k));
    delete axios.defaults.headers.common['X-Subject-Id'];
  } catch { /* private mode */ }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem('futurepath_token') || localStorage.getItem('futurepath_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  // Pull down which page overviews this person has already seen, so a new browser
  // doesn't make them sit through every arrival cover again.
  useEffect(() => {
    if (user?.id && token) syncArrivals(user.id, token);
  }, [user?.id, token]);

  const checkAuth = async () => {
    // An explicitly stored token wins over the cookie session. Fourteen screens hand-install a
    // token (lab joins, claim-profile, org activation, demo switching) and on a shared machine the
    // previous person's Google cookie was still winning the race — so a brand-new account booted
    // as somebody else and hit their read-only wall. Deliberate beats leftover.
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        rememberUser(response.data?.id);
        setUser(response.data);
        setLoading(false);
        return;
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    }

    // Cookie session (Google OAuth) is the fallback.
    try {
      const response = await axios.get(`${API}/auth/me/session`, {
        withCredentials: true
      });
      rememberUser(response.data?.id);
      setUser(response.data);
      setLoading(false);
      return;
    } catch (e) {
      // No session either.
    }

    if (token) logout();
    setLoading(false);
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('futurepath_token', access_token);
    rememberUser(userData?.id);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('futurepath_token', access_token);
    // A new account starts as itself. Never inherit whoever used this browser last.
    clearSubjectSelections();
    rememberUser(userData?.id);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleCallback = async (sessionId) => {
    const response = await axios.get(`${API}/auth/session?session_id=${sessionId}`, {
      withCredentials: true
    });
    rememberUser(response.data.user?.id);
    setUser(response.data.user);
    return response.data.user;
  };

  const logout = async () => {
    const wasLab = user?.account_type === 'lab';
    const labCode = localStorage.getItem('futurepath_labs_code');
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    localStorage.removeItem('futurepath_token');
    sessionStorage.removeItem('futurepath_token');
    localStorage.removeItem('futurepath_uid');
    clearArrivals();
    clearSubjectSelections();
    setToken(null);
    setUser(null);
    if (wasLab) {
      window.location.href = labCode ? `/AFPLabs/join/${labCode}` : '/AFPLabs';
    }
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  // Memoised: many pages put getAuthHeader in useCallback/useEffect dependency
  // arrays. A fresh function every render turned those into infinite request
  // loops (this is what crashed College Intelligence).
  const getAuthHeader = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true
  }), [token]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      loginWithGoogle,
      processGoogleCallback,
      logout,
      updateUser,
      refreshUser,
      getAuthHeader,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};
